import React from "react";

/**
 * Loader - the single brand-consistent loading indicator for the app.
 *
 * Variants:
 *   • "splash"    - full-screen overlay with the brand logo (use on initial bootstrap)
 *   • "page"      - fits inside a page section / route (default for tab swaps)
 *   • "inline"    - small inline spinner with optional label
 *   • "skeleton"  - render a grid of pulsing cards (`count` controls how many)
 */
export default function Loader({
  variant = "page",
  label = "Loading",
  count = 4,
  fullscreen = false,
}) {
  if (variant === "splash") {
    return <SplashLoader label={label} />;
  }

  if (variant === "skeleton") {
    return (
      <div
        data-testid="loader-skeleton"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6"
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-fscs-border p-6 animate-pulse"
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 rounded-full bg-fscs-bg" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-fscs-bg w-3/4" />
                <div className="h-2.5 bg-fscs-bg w-1/2" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-2.5 bg-fscs-bg w-full" />
              <div className="h-2.5 bg-fscs-bg w-5/6" />
              <div className="h-2.5 bg-fscs-bg w-3/4" />
            </div>
          </div>
        ))}
        <span className="sr-only">{label}…</span>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <span
        data-testid="loader-inline"
        className="inline-flex items-center gap-2.5 text-fscs-muted text-sm"
        role="status"
        aria-live="polite"
      >
        <Spinner />
        <span>{label}…</span>
      </span>
    );
  }

  // "page" variant - centered, full vertical block
  return (
    <div
      data-testid="loader-page"
      className={`${
        fullscreen ? "min-h-screen" : "min-h-[60vh]"
      } flex flex-col items-center justify-center gap-4 text-fscs-muted`}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <Spinner size={32} />
      <p className="fscs-label">{label}…</p>
    </div>
  );
}

// ------------------------- Splash (initial bootstrap) -------------------------
function SplashLoader({ label }) {
  return (
    <div
      data-testid="loader-splash"
      className="fixed inset-0 z-[60] bg-fscs-bg flex flex-col items-center justify-center gap-8 px-6"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <img
        src="/assets/logo-white.png"
        alt="Florida Cosmetic Surgeons"
        className="h-20 sm:h-24 w-auto opacity-0 animate-[fadeIn_0.5s_ease_forwards]"
      />

      <div className="flex flex-col items-center gap-4">
        <Spinner size={28} />
        <p className="fscs-label text-fscs-muted">{label}…</p>
      </div>

      <style>{`
        @keyframes fadeIn { to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

// ------------------------- Animated spinner -------------------------
function Spinner({ size = 22 }) {
  const stroke = Math.max(2, Math.round(size / 11));
  const r = (size - stroke) / 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="animate-spin"
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#FF7A6B"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${r * 2.4} ${r * 4}`}
      />
    </svg>
  );
}

